import { supabase } from '@/lib/supabase'

export type MonitorStatus = 'red' | 'yellow' | 'green' | 'unknown' | 'off' | string

export interface MonitorSnapshot {
  generated_at?: string
  scanned_at?: string | null
  colour_rules?: string
  blind_spots?: string[]
  totals?: { red?: number; yellow?: number; green?: number; unknown?: number; modules?: number }
  unknown_by_type?: Record<string, number>
  modules?: MonitorModule[]
  platform?: MonitorBucket
  unmapped?: MonitorBucket
  efs?: MonitorEf[]
  integrations?: MonitorIntegration[]
  cron?: MonitorCron[]
  checks?: MonitorCheck[]
  infra?: MonitorInfra
  faults?: unknown[]
  alerts?: unknown[]
  scans?: MonitorScan[]
  running?: MonitorScan | null
}

export interface MonitorModule {
  module_code: string
  name_he?: string
  status: MonitorStatus
  efs_ok?: number
  efs_total?: number
  efs_unknown?: number
  crons_ok?: number
  crons_total?: number
  integrations?: unknown
  checks?: unknown
  errors_24h?: number | null
  last_fault?: string | null
  last_fault_at?: string | null
  last_fault_title?: string | null
}

export interface MonitorBucket {
  status: MonitorStatus
  count_efs?: number
  count_crons?: number
  last_fault_at?: string | null
  last_fault_title?: string | null
  efs?: MonitorEf[]
  crons?: MonitorCron[]
}

export interface MonitorDiffRow {
  type?: string
  key?: string
  project?: string
  status?: MonitorStatus
  prior_status?: MonitorStatus
  from?: MonitorStatus
  to?: MonitorStatus
}

export interface MonitorScanDiff {
  error?: string
  run_id?: string
  prior_run_id?: string | null
  worse?: MonitorDiffRow[]
  better?: MonitorDiffRow[]
  new?: MonitorDiffRow[]
  gone?: MonitorDiffRow[]
}

export interface MonitorEf {
  project?: string
  function_name?: string
  module_code?: string
  invocations_1h?: number
  errors_5xx_1h?: number
  errors_4xx_1h?: number
  error_pct?: number | null
  top_error?: string | null
  status?: MonitorStatus
  verify_jwt?: boolean | null
  evidence?: string | null
  key?: string
}

export interface MonitorIntegration {
  provider?: string
  module_code?: string
  ok?: boolean | null
  latency_ms?: number | null
  error?: string | null
  ran_at?: string | null
  orgs?: number | null
  status?: MonitorStatus
  critical?: boolean
  evidence?: string | null
}

export interface MonitorCron {
  jobname?: string
  schedule?: string
  active?: boolean
  module_code?: string
  last_status?: string | null
  last_start?: string | null
  fails_7d?: number | null
  failed_24h?: boolean | null
  hardcoded_jwt?: boolean
  status?: MonitorStatus
  evidence?: string | null
}

export interface MonitorCheck {
  key?: string
  module_code?: string
  title_he?: string
  value?: number | null
  level?: string | null
  warn_at?: number | null
  crit_at?: number | null
  ran_at?: string | null
  status?: MonitorStatus
  evidence?: string | null
}

export interface MonitorInfra {
  db_conns?: number | null
  slow_queries?: number | null
  wa_canary_ok?: boolean | null
  advisors_at?: string | null
  rls_off_dgxn?: unknown
}

export interface MonitorScan {
  id?: string
  started_at?: string
  finished_at?: string | null
  trigger?: string
  mode?: string
  step?: string | null
  steps?: Record<string, unknown> | null
  module?: string | null
}

export interface ScanStart {
  ok?: boolean
  started?: boolean
  run_id?: string
  mode?: string
  status?: number
  error?: string
}

export const monitorApi = {
  snapshot: async (): Promise<MonitorSnapshot> => {
    const { data, error } = await supabase.rpc('admin_monitor_snapshot')
    if (error) throw error
    return (data ?? {}) as MonitorSnapshot
  },

  startFullScan: async (): Promise<ScanStart> => {
    const { data, error } = await supabase.functions.invoke('monitor-scan', {
      body: { mode: 'full', trigger: 'manual' },
    })
    if (error) {
      const response = (error as { context?: Response }).context
      let body: ScanStart = {}
      if (response) {
        body = await response.clone().json().catch(() => ({}))
      }
      return { ...body, ok: false, status: response?.status, error: body.error ?? error.message }
    }
    return (data ?? {}) as ScanStart
  },

  diff: async (runId: string): Promise<MonitorScanDiff> => {
    const { data, error } = await supabase.rpc('admin_monitor_scan_diff', { p_run_id: runId })
    if (error) throw error
    return (data ?? {}) as MonitorScanDiff
  },
}
